import * as React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import WarningIcon from '@mui/icons-material/Warning';
import { createCheckoutSession } from '../../../components/business_logic/payments_requests';
import { userGetLicenseKeysPaginated } from '../../../components/business_logic/license_requests';
import { LicenseKey } from '../../../models/license';

interface CheckoutProps {
    inDialog?: boolean;
    onSuccess?: () => void;
}

export default function Checkout({ inDialog = false, onSuccess }: CheckoutProps) {
    const [isCheckingLicense, setIsCheckingLicense] = React.useState(true);
    const [paymentError, setPaymentError] = React.useState<string | null>(null);
    const [showWarningDialog, setShowWarningDialog] = React.useState(false);

    // Check for existing active license before proceeding
    React.useEffect(() => {
        checkExistingLicense().then();
    }, []);

    const checkExistingLicense = async () => {
        setIsCheckingLicense(true);
        try {
            const licenses = await userGetLicenseKeysPaginated(1);
            // Check if user has an active license with a payment
            const activeLicenseWithPayment = licenses.find(
                (license: LicenseKey) => license.is_active && license.payment
            );
            
            if (activeLicenseWithPayment) {
                setShowWarningDialog(true);
            } else {
                // No active license with payment, proceed to checkout
                createCheckoutAndRedirect().then();
            }
        } catch (error) {
            console.error('Error checking existing license:', error);
            // If check fails, proceed anyway (don't block user)
            createCheckoutAndRedirect().then();
        } finally {
            setIsCheckingLicense(false);
        }
    };

    const handleWarningConfirm = () => {
        setShowWarningDialog(false);
        createCheckoutAndRedirect().then();
    };

    const handleWarningCancel = () => {
        setShowWarningDialog(false);
        if (onSuccess) {
            onSuccess(); // Close dialog if in dialog mode
        }
    };

    const createCheckoutAndRedirect = async () => {
        setPaymentError(null);
        
        try {
            // Create checkout session using the business logic function
            // Backend will handle URL construction if not provided, but we'll provide them explicitly
            const checkoutResponse = await createCheckoutSession({
                success_url: `${window.location.origin}/payment/success?session_id={CHECKOUT_SESSION_ID}`,
                cancel_url: `${window.location.origin}/payment/cancel`,
            });

            // Redirect to Stripe's hosted checkout page
            if (checkoutResponse.checkout_url) {
                window.location.href = checkoutResponse.checkout_url;
            } else {
                // Log the full response for debugging
                console.error('Invalid checkout session response:', checkoutResponse);
                throw new Error('Invalid checkout session response: missing checkout_url. Please check the server response.');
            }
        } catch (error: any) {
            const errorMessage = error.message || 'Failed to initialize payment. Please try again.';
            setPaymentError(errorMessage);
            console.error('Error creating checkout session:', error);
        }
    };

    return (
        <>
            <Container sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: inDialog ? '400px' : '60vh' }}>
                <Box sx={{ textAlign: 'center', maxWidth: 500 }}>
                    {isCheckingLicense ? (
                        <Stack spacing={3} alignItems="center">
                            <CircularProgress size={60} />
                            <Typography variant="h6" component="h2">
                                Checking your account...
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Please wait while we verify your license status.
                            </Typography>
                        </Stack>
                    ) : paymentError ? (
                        <Stack spacing={2}>
                            <Alert severity="error" onClose={() => setPaymentError(null)}>
                                {paymentError}
                            </Alert>
                        </Stack>
                    ) : (
                        <Stack spacing={3} alignItems="center">
                            <CircularProgress size={60} />
                            <Typography variant="h6" component="h2">
                                Redirecting to checkout...
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Please wait while we prepare your payment session.
                            </Typography>
                        </Stack>
                    )}
                </Box>
            </Container>

            {/* Warning Dialog for Existing License */}
            <Dialog
                open={showWarningDialog}
                onClose={handleWarningCancel}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon color="warning" />
                    <Typography variant="h6">You Already Have an Active License</Typography>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <Typography variant="body1">
                            You already have an active license key associated with a payment. 
                            Continuing with this purchase will charge you again for a brand new license.
                        </Typography>
                        <Alert severity="warning">
                            <Typography variant="body2" fontWeight="bold">
                                Important:
                            </Typography>
                            <Typography variant="body2">
                                This will create a separate license key. Your existing license will remain active. 
                                You will be charged the full license price again.
                            </Typography>
                        </Alert>
                        <Typography variant="body2" color="text.secondary">
                            Are you sure you want to proceed with purchasing a new license?
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleWarningCancel} variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={handleWarningConfirm} variant="contained" color="primary">
                        Yes, Continue to Checkout
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}